import { ForwardedRef, forwardRef, HTMLProps } from 'react';
import {
  StyledCheckbox,
  StyledCheckboxLabel,
  StyledCheckboxLabelText,
  StyledCheckboxWrapper,
  StyledDescription,
} from './Checkbox.styles';

export interface CheckboxProps
  extends Omit<HTMLProps<HTMLInputElement>, 'label'> {
  /** Primary label for the checkbox */
  label: string | JSX.Element;
  /** ID for the checkbox */
  id: string;
  /** Optional description for the checkbox, appears below the label */
  description?: string;
  /** Props to pass to the label element */
  labelProps?: HTMLProps<HTMLLabelElement>;
  /** Optionally change the checkbox colour */
  color?: string;
  testId?: string;
  labelVisible?: boolean;
}

/**
 * A thin wrapper around <input type="checkbox" />.
 * to the input except for the following:
 *
 * * label
 * * labelProps
 * * description
 *
 * IDs and aria attributes for the label (and optional description) will be set
 * automatically based on the provided ID.
 *
 * TODO: other states e.g. errors, disabled etc
 */
export const Checkbox = forwardRef(
  (
    {
      label,
      id,
      description,
      labelProps,
      testId,
      color,
      labelVisible = true,
      ...otherProps
    }: CheckboxProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    delete otherProps.type;
    return (
      <StyledCheckboxWrapper>
        <StyledCheckbox
          type="checkbox"
          id={id}
          ref={ref}
          $color={color}
          aria-labelledby={`${id}-label`}
          aria-describedby={description ? `${id}-description` : undefined}
          {...otherProps}
        />
        <StyledCheckboxLabel
          id={`${id}-label`}
          htmlFor={id}
          $color={color}
          {...labelProps}
          $visible={labelVisible}
        >
          <StyledCheckboxLabelText $visible={labelVisible}>
            {label}
          </StyledCheckboxLabelText>
        </StyledCheckboxLabel>
        {description && (
          <StyledDescription id={`${id}-description`}>
            {description}
          </StyledDescription>
        )}
      </StyledCheckboxWrapper>
    );
  },
);

export default Checkbox;
